import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import ViewsNav from '../components/ViewsNav';
import { setTaskDone } from '../lib/tasks';
import TaskCard from '../components/TaskCard';
import TaskModal from '../components/TaskModal';
import PropertyInfoEditor from '../components/PropertyInfoEditor';
import PropertyPhotoUpload from '../components/PropertyPhotoUpload';
import PropertyDocUpload from '../components/PropertyDocUpload';

const TABS = ['Overview', 'Photos', 'Documents', 'History', 'Open Issues', 'Notes'];
const PHOTO_CATEGORIES = ['All', 'Exterior', 'Interior', 'Kitchen', 'Bathroom', 'Bedroom', 'Other'];

const PROPERTY_THEMES = {
  "It's Happy Hour": { accent: '#00B4C6', light: '#e0f7fa', text: '#00838f' },
  '4400 Windswept': { accent: '#4A7C59', light: '#e8f5ee', text: '#3a6347' },
};

export default function PropertyHub({ session, propertyId, currentView, onNavigate }) {
  const [property, setProperty] = useState(null);
  const [properties, setProperties] = useState([]);
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(true);

  // Photos tab
  const [photos, setPhotos] = useState([]);
  const [photoFilter, setPhotoFilter] = useState('All');
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // Documents tab
  const [documents, setDocuments] = useState([]);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [docLightboxIndex, setDocLightboxIndex] = useState(-1);

  // Tasks
  const [completedTasks, setCompletedTasks] = useState([]);
  const [openTasks, setOpenTasks] = useState([]);
  const [taskPhotos, setTaskPhotos] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);

  // Notes tab
  const [notes, setNotes] = useState('');
  const [notesId, setNotesId] = useState(null);
  const [notesSaving, setNotesSaving] = useState(false);

  const theme = PROPERTY_THEMES[property?.name] || { accent: '#7B3F6E', light: '#f0e4ee', text: '#7B3F6E' };

  const fetchProperty = useCallback(async () => {
    const { data } = await supabase.from('properties').select('*').eq('id', propertyId).single();
    if (data) setProperty(data);
  }, [propertyId]);

  const fetchProperties = async () => {
    const { data } = await supabase.from('properties').select('*').order('name');
    if (data) setProperties(data);
  };

  const fetchPhotos = useCallback(async () => {
    const { data } = await supabase
      .from('property_photos')
      .select('*')
      .eq('property_id', propertyId)
      .order('created_at', { ascending: false });
    if (data && data.length > 0) {
      const withUrls = await Promise.all(data.map(async (p) => {
        const { data: urlData } = await supabase.storage
          .from('property-photos')
          .createSignedUrl(p.storage_path, 3600);
        return { ...p, url: urlData?.signedUrl };
      }));
      setPhotos(withUrls);
    } else {
      setPhotos([]);
    }
  }, [propertyId]);

  const fetchDocuments = useCallback(async () => {
    const { data } = await supabase
      .from('property_documents')
      .select('*')
      .eq('property_id', propertyId)
      .order('created_at', { ascending: false });
    if (data && data.length > 0) {
      const withUrls = await Promise.all(data.map(async (doc) => {
        const { data: urlData } = await supabase.storage
          .from('property-documents')
          .createSignedUrl(doc.storage_path, 3600);
        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.filename || '');
        return { ...doc, url: urlData?.signedUrl, isImage };
      }));
      setDocuments(withUrls);
    } else {
      setDocuments([]);
    }
  }, [propertyId]);

  const fetchTasks = useCallback(async () => {
    const { data: completed } = await supabase
      .from('tasks')
      .select('*, properties(name)')
      .eq('property_id', propertyId)
      .eq('done', true)
      .order('updated_at', { ascending: false });
    if (completed) setCompletedTasks(completed);

    const { data: open } = await supabase
      .from('tasks')
      .select('*, properties(name)')
      .eq('property_id', propertyId)
      .eq('done', false)
      .order('created_at', { ascending: false });
    if (open) setOpenTasks(open);
  }, [propertyId]);

  const fetchTaskPhotos = useCallback(async () => {
    const { data } = await supabase
      .from('task_photos')
      .select('*')
      .order('created_at');
    if (data && data.length > 0) {
      const withUrls = await Promise.all(data.map(async (p) => {
        const { data: urlData } = await supabase.storage
          .from('task-photos')
          .createSignedUrl(p.storage_path, 3600);
        return { ...p, url: urlData?.signedUrl };
      }));
      setTaskPhotos(withUrls);
    } else {
      setTaskPhotos([]);
    }
  }, []);

  const fetchNotes = useCallback(async () => {
    const { data } = await supabase
      .from('property_notes')
      .select('*')
      .eq('property_id', propertyId)
      .limit(1);
    if (data && data.length > 0) {
      setNotes(data[0].content || '');
      setNotesId(data[0].id);
    } else {
      setNotes('');
      setNotesId(null);
    }
  }, [propertyId]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchProperty(),
        fetchProperties(),
        fetchPhotos(),
        fetchDocuments(),
        fetchTasks(),
        fetchTaskPhotos(),
        fetchNotes(),
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchProperty, fetchPhotos, fetchDocuments, fetchTasks, fetchTaskPhotos, fetchNotes]);

  const saveNotes = async () => {
    if (notesSaving) return;
    setNotesSaving(true);
    if (notesId) {
      await supabase.from('property_notes').update({
        content: notes,
        updated_at: new Date().toISOString(),
      }).eq('id', notesId);
    } else if (notes.trim()) {
      const { data } = await supabase.from('property_notes').insert({
        property_id: propertyId,
        content: notes,
      }).select().single();
      if (data) setNotesId(data.id);
    }
    setNotesSaving(false);
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm('Delete this photo?')) return;
    await supabase.storage.from('property-photos').remove([photo.storage_path]);
    await supabase.from('property_photos').delete().eq('id', photo.id);
    setPhotos(photos.filter(p => p.id !== photo.id));
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm('Delete this document?')) return;
    if (doc.storage_path) {
      await supabase.storage.from('property-documents').remove([doc.storage_path]);
    }
    await supabase.from('property_documents').delete().eq('id', doc.id);
    setDocuments(documents.filter(d => d.id !== doc.id));
  };

  const downloadDocument = async (doc) => {
    if (doc.url) {
      window.open(doc.url, '_blank');
    } else {
      const { data } = await supabase.storage
        .from('property-documents')
        .createSignedUrl(doc.storage_path, 300);
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    }
  };

  const toggleTask = async (task) => {
    await setTaskDone(task, !task.done);
    await fetchTasks();
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await supabase.from('tasks').delete().eq('id', id);
    await fetchTasks();
  };

  const onTaskSaved = async () => {
    setShowTaskModal(false);
    await fetchTasks();
    await fetchTaskPhotos();
  };

  // Stats for overview
  const openTaskCount = openTasks.length;
  const ytdCost = [...completedTasks, ...openTasks]
    .reduce((sum, t) => sum + (parseFloat(t.cost) || 0), 0);

  // Filter photos
  const filteredPhotos = photoFilter === 'All'
    ? photos
    : photos.filter(p => p.category === photoFilter);

  if (loading || !property) {
    return <div className="loading">Loading property...</div>;
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'Overview':
        return (
          <PropertyInfoEditor
            property={property}
            theme={theme}
            stats={{ openTasks: openTaskCount, ytdCost }}
            onSaved={(updated) => setProperty(updated)}
          />
        );

      case 'Photos':
        return (
          <div>
            <div className="hub-tab-toolbar">
              <div className="hub-filter-row">
                {PHOTO_CATEGORIES.map(c => (
                  <button
                    key={c}
                    className={`btn btn-sm ${photoFilter === c ? 'btn-primary' : 'btn-secondary'}`}
                    style={photoFilter === c ? { background: theme.accent } : {}}
                    onClick={() => setPhotoFilter(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <button
                className="btn btn-primary btn-sm"
                style={{ background: theme.accent }}
                onClick={() => setShowPhotoUpload(true)}
              >
                + Upload Photos
              </button>
            </div>

            {filteredPhotos.length === 0 ? (
              <div className="empty-state">
                <h3>No photos</h3>
                <p>Upload photos to document this property</p>
              </div>
            ) : (
              <div className="property-photo-grid">
                {filteredPhotos.map((photo, i) => (
                  <div key={photo.id} className="property-photo-item">
                    <img
                      src={photo.url}
                      alt={photo.caption || photo.filename}
                      onClick={() => setLightboxIndex(i)}
                    />
                    <div className="property-photo-info">
                      {photo.caption && <span className="property-photo-caption">{photo.caption}</span>}
                      <span className="tag" style={{ background: theme.light, color: theme.text }}>{photo.category}</span>
                      <button className="icon-btn delete" onClick={() => deletePhoto(photo)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'Documents':
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <button
                className="btn btn-primary btn-sm"
                style={{ background: theme.accent }}
                onClick={() => setShowDocUpload(true)}
              >
                + Upload Document
              </button>
            </div>

            {documents.length === 0 ? (
              <div className="empty-state">
                <h3>No documents</h3>
                <p>Upload insurance, HOA, warranty, or other documents</p>
              </div>
            ) : (
              <div className="doc-list">
                {documents.map((doc, i) => (
                  <div key={doc.id} className="doc-item">
                    {doc.isImage && doc.url ? (
                      <img
                        className="doc-thumbnail"
                        src={doc.url}
                        alt={doc.title}
                        onClick={() => setDocLightboxIndex(i)}
                      />
                    ) : (
                      <div className="doc-icon">
                        {/\.pdf$/i.test(doc.filename || '') ? 'PDF' : 'DOC'}
                      </div>
                    )}
                    <div className="doc-info">
                      <span
                        className="doc-title"
                        onClick={() => doc.isImage ? setDocLightboxIndex(i) : downloadDocument(doc)}
                      >
                        {doc.title}
                      </span>
                      <div className="doc-meta">
                        <span className="tag" style={{ background: theme.light, color: theme.text }}>{doc.category}</span>
                        <span className="doc-date">
                          {new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        {doc.filename && <span className="doc-filename">{doc.filename}</span>}
                      </div>
                    </div>
                    <div className="doc-actions">
                      <button className="icon-btn" onClick={() => doc.isImage ? setDocLightboxIndex(i) : downloadDocument(doc)}>View</button>
                      <button className="icon-btn delete" onClick={() => deleteDocument(doc)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'History':
        return (
          <div>
            {completedTasks.length === 0 ? (
              <div className="empty-state">
                <h3>No completed maintenance</h3>
                <p>Completed tasks will appear here as a historical record</p>
              </div>
            ) : (
              <>
                <div className="history-list">
                  {completedTasks.map(task => (
                    <div key={task.id} className="history-item">
                      <div className="history-title">{task.title}</div>
                      <div className="history-meta">
                        {task.vendor && <span className="tag tag-vendor">{task.vendor}</span>}
                        {task.cost > 0 && <span className="tag tag-cost">${parseFloat(task.cost).toLocaleString()}</span>}
                        {task.updated_at && (
                          <span className="history-date">
                            {new Date(task.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="history-total" style={{ borderTopColor: theme.light }}>
                  <strong>Total Cost:</strong>
                  <span style={{ color: theme.accent, fontWeight: 700 }}>
                    ${completedTasks.reduce((sum, t) => sum + (parseFloat(t.cost) || 0), 0).toLocaleString()}
                  </span>
                </div>
              </>
            )}
          </div>
        );

      case 'Open Issues':
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <button
                className="btn btn-primary btn-sm"
                style={{ background: theme.accent }}
                onClick={() => setShowTaskModal(true)}
              >
                + Add Task
              </button>
            </div>

            {openTasks.length === 0 ? (
              <div className="empty-state">
                <h3>No open issues</h3>
                <p>All caught up! Add a task if something comes up.</p>
              </div>
            ) : (
              <div className="task-list">
                {openTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    photos={taskPhotos.filter(p => p.task_id === task.id)}
                    onToggle={toggleTask}
                    onEdit={() => {}}
                    onDelete={deleteTask}
                  />
                ))}
              </div>
            )}
          </div>
        );

      case 'Notes':
        return (
          <div>
            <textarea
              className="property-notes-editor"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={saveNotes}
              placeholder="Add property notes... Owner preferences, quirks, seasonal notes, guest tips..."
            />
            <p className="property-notes-status">
              {notesSaving ? 'Saving...' : 'Auto-saves when you click away'}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Property Manager</h1>
          <p>{session.user.email}</p>
        </div>

        <ViewsNav currentView={currentView} onNavigate={onNavigate} />

        <div className="sidebar-section">
          <div className="sidebar-section-label">Properties</div>
          {properties.map(p => (
            <div
              key={p.id}
              className={`sidebar-item ${p.id === propertyId ? 'active' : ''}`}
              onClick={() => onNavigate('property-hub', p.id)}
            >
              {p.name}
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={() => supabase.auth.signOut()}>Sign Out</button>
        </div>
      </aside>

      <main className="main-content">
        <div className="hub-header">
          <button className="btn btn-secondary btn-sm" onClick={() => onNavigate('tasks')}>
            &larr; Back to Tasks
          </button>
          <h1 className="hub-title" style={{ color: theme.accent }}>{property.name}</h1>
        </div>

        <div className="hub-tabs">
          {TABS.map(tab => (
            <button
              key={tab}
              className={`hub-tab ${activeTab === tab ? 'active' : ''}`}
              style={activeTab === tab ? { borderBottomColor: theme.accent, color: theme.accent } : {}}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="content-area">
          {renderTab()}
        </div>
      </main>

      {showPhotoUpload && (
        <PropertyPhotoUpload
          propertyId={propertyId}
          onUploaded={() => { setShowPhotoUpload(false); fetchPhotos(); }}
          onClose={() => setShowPhotoUpload(false)}
        />
      )}

      {showDocUpload && (
        <PropertyDocUpload
          propertyId={propertyId}
          onUploaded={() => { setShowDocUpload(false); fetchDocuments(); }}
          onClose={() => setShowDocUpload(false)}
        />
      )}

      {showTaskModal && (
        <TaskModal
          task={null}
          properties={properties}
          defaultPropertyId={propertyId}
          onSaved={onTaskSaved}
          onClose={() => setShowTaskModal(false)}
        />
      )}

      {docLightboxIndex >= 0 && documents[docLightboxIndex]?.isImage && (
        <div className="lightbox-overlay" onClick={() => setDocLightboxIndex(-1)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setDocLightboxIndex(-1)}>&times;</button>
            <img
              className="lightbox-image"
              src={documents[docLightboxIndex].url}
              alt={documents[docLightboxIndex].title}
            />
            <p style={{ color: 'white', marginTop: 8, fontSize: 14 }}>
              {documents[docLightboxIndex].title}
            </p>
            {documents.filter(d => d.isImage).length > 1 && (
              <div className="lightbox-nav">
                <button
                  className="lightbox-arrow"
                  onClick={() => {
                    const imageIndices = documents.map((d, idx) => d.isImage ? idx : -1).filter(idx => idx >= 0);
                    const currentPos = imageIndices.indexOf(docLightboxIndex);
                    setDocLightboxIndex(imageIndices[(currentPos - 1 + imageIndices.length) % imageIndices.length]);
                  }}
                >&lsaquo;</button>
                <span className="lightbox-counter">
                  {documents.filter(d => d.isImage).indexOf(documents[docLightboxIndex]) + 1} of {documents.filter(d => d.isImage).length}
                </span>
                <button
                  className="lightbox-arrow"
                  onClick={() => {
                    const imageIndices = documents.map((d, idx) => d.isImage ? idx : -1).filter(idx => idx >= 0);
                    const currentPos = imageIndices.indexOf(docLightboxIndex);
                    setDocLightboxIndex(imageIndices[(currentPos + 1) % imageIndices.length]);
                  }}
                >&rsaquo;</button>
              </div>
            )}
          </div>
        </div>
      )}

      {lightboxIndex >= 0 && filteredPhotos.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setLightboxIndex(-1)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxIndex(-1)}>&times;</button>
            <img
              className="lightbox-image"
              src={filteredPhotos[lightboxIndex].url}
              alt={filteredPhotos[lightboxIndex].caption || 'Photo'}
            />
            {filteredPhotos[lightboxIndex].caption && (
              <p style={{ color: 'white', marginTop: 8, fontSize: 14 }}>
                {filteredPhotos[lightboxIndex].caption}
              </p>
            )}
            {filteredPhotos.length > 1 && (
              <div className="lightbox-nav">
                <button
                  className="lightbox-arrow"
                  onClick={() => setLightboxIndex((lightboxIndex - 1 + filteredPhotos.length) % filteredPhotos.length)}
                >&lsaquo;</button>
                <span className="lightbox-counter">{lightboxIndex + 1} of {filteredPhotos.length}</span>
                <button
                  className="lightbox-arrow"
                  onClick={() => setLightboxIndex((lightboxIndex + 1) % filteredPhotos.length)}
                >&rsaquo;</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
